/** Who is signed in, what they may do, and the words for every enum. */
import { z } from 'zod';
import { roleSchema, userStatusSchema } from './enums';

export const userSchema = z.object({
	pkUserId: z.string(),
	email: z.string(),
	name: z.string(),
	jobTitle: z.string(),
	role: roleSchema,
	status: userStatusSchema,
	lastLoginAt: z.string().nullable(),
	fkFirmId: z.string(),
	firmName: z.string(),
	firmSlug: z.string(),
});

/**
 * What this user may do, computed by the API and sent with the session. Never
 * derived from the role here: a second copy of that mapping would drift from
 * the server's. `accessIntake` and `accessAudits` gate places, not capabilities.
 */
export const permissionsSchema = z.object({
	accessIntake: z.boolean(),
	accessAudits: z.boolean(),
	uploadFiles: z.boolean(),
	mapFormats: z.boolean(),
	runAnalyses: z.boolean(),
	generateReports: z.boolean(),
	manageUsers: z.boolean(),
	manageClients: z.boolean(),
	deleteData: z.boolean(),
});

export const sessionSchema = z.object({
	user: userSchema,
	permissions: permissionsSchema,
});

export const termSchema = z.object({
	label: z.string(),
	tone: z.enum(['good', 'warn', 'bad', 'busy', 'neutral']).optional(),
	help: z.string().optional(),
});

/**
 * Fixed group keys, not a `z.record` over them: `useTerm` is typed against
 * these eleven, and a record would let any group name through.
 */
export const vocabularySchema = z.object({
	fileStatus: z.record(z.string(), termSchema),
	runStatus: z.record(z.string(), termSchema),
	analysisStatus: z.record(z.string(), termSchema).catch({}),
	role: z.record(z.string(), termSchema),
	userStatus: z.record(z.string(), termSchema),
	auditStatus: z.record(z.string(), termSchema),
	exceptionStatus: z.record(z.string(), termSchema),
	reportStatus: z.record(z.string(), termSchema),
	severity: z.record(z.string(), termSchema),
	classification: z.record(z.string(), termSchema),
	checkStatus: z.record(z.string(), termSchema),
	layoutMatch: z.record(z.string(), termSchema),
	activityAction: z.record(z.string(), termSchema).catch({}),
	rulesetStatus: z.record(z.string(), termSchema).catch({}),
});

export type User = z.infer<typeof userSchema>;
export type Permissions = z.infer<typeof permissionsSchema>;
export type Session = z.infer<typeof sessionSchema>;
export type Term = z.infer<typeof termSchema>;
export type Vocabulary = z.infer<typeof vocabularySchema>;
